import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBarcode, faCaretDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { SignalFieldBaseDirective } from '../signal-field-base.directive';
import { SelectFieldOption } from '../select-field-option';

/** option count above which the menu offers a search box, as in the reactive-forms dropdown */
const defaultSearchThreshold = 3;

/**
 * Low-level dropdown for signal forms.
 *
 * Two firsts, which is why it is worth reading before the simpler fields: its value is a
 * **collection** (`string[]`, the selected option values) and it wraps **no native
 * control** at all — the trigger, the menu and the tags are all ordinary elements. The
 * `[formField]` contract turns out not to care about either: `FormValueControl<string[]>`
 * binds exactly as the scalar fields do.
 *
 * ```html
 * <pace-core-dropdown-field [formField]="signalForm.workCode" [options]="workCodes()" />
 * ```
 *
 * A single-select dropdown is just `maxSelections = 1`, so the value shape does not
 * change with the mode and neither does the slice. "At least one selection" belongs in
 * the schema as `minLength(path, 1)` rather than `required`, which is ambiguous on a
 * collection — and both length rules reach this component through the contract.
 *
 * Not carried over from the reactive-forms `DropdownComponent`: the `displayText`
 * free-text mode (a combobox, which deserves its own component), `autoSelect` and
 * `alwaysSelectedEntries` (container policy, not presentation) and `invalidSelection`
 * (a validation concern, so a schema rule). Arrow-key navigation of the menu is also
 * still missing; Escape closes it and the options are reachable by tab.
 */
@Component({
    selector: 'pace-dropdown-field',
    templateUrl: './dropdown-field.component.html',
    styleUrls: ['./dropdown-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent, TranslateModule],
    host: {
        '(document:click)': 'onDocumentClick($event)',
    },
})
export class DropdownFieldComponent extends SignalFieldBaseDirective<string[]> {
    /** the selected option values, in selection order */
    public readonly value = model<string[]>([]);

    // --- `FormUiControl` inputs, all bound automatically by `[formField]` -----

    /** lower bound on the number of selections, from the schema's `minLength` rule */
    public readonly minLength = input<number | undefined>(undefined);

    /** upper bound on the number of selections, from the schema's `maxLength` rule */
    public readonly maxLength = input<number | undefined>(undefined);

    // --- own inputs ----------------------------------------------------------

    public readonly options = input<readonly SelectFieldOption[]>([]);

    /** 1 makes this a single-select; the schema's `maxLength` wins over it when smaller */
    public readonly maxSelections = input(1);

    /**
     * Whether picking the chosen option again takes it back off.
     *
     * On by default, because clearing a choice that way is how the reactive-forms dropdown behaved and
     * a field is free to be left empty and invalid — validity is the schema's business, not this
     * control's. A container turns it off when its value stands for something that always exists, such
     * as which record is on screen: those containers ignore an empty choice, and ignoring it silently
     * is what strands the control, since the value they bind never changed and so is never written
     * back down. Refusing the gesture here is the only place where nothing has diverged yet.
     */
    public readonly deselectable = input(true);

    public readonly placeholder = input('');

    /** option count above which the menu offers a search box */
    public readonly searchThreshold = input(defaultSearchThreshold);

    // --- barcode scanning ----------------------------------------------------

    public readonly scanFn = input<(() => Observable<string>) | undefined>(undefined);

    /** shows the scan button */
    public readonly scannable = computed(() => this.scanFn() !== undefined);

    /** the value of the option a scan resolved to */
    public readonly scanResults = output<string>();

    /** emitted when the scanned text matches no option */
    public readonly scanNoMatch = output<string>();

    public readonly faBarcode = faBarcode;
    public readonly faCaretDown = faCaretDown;
    public readonly faTimes = faTimes;

    private readonly destroyRef = inject(DestroyRef);
    private readonly elementRef = inject(ElementRef);

    protected readonly isOpen = signal(false);
    protected readonly searchTerm = signal('');

    /** the effective cap: whichever of the schema rule and the input is stricter */
    protected readonly selectionLimit = computed(() => Math.min(this.maxSelections(), this.maxLength() ?? Infinity));

    protected readonly isMultiSelect = computed(() => this.selectionLimit() > 1);

    protected readonly selectedOptions = computed(() => {
        const options = this.options();

        // driven by the value so the tags follow selection order, not option order
        return this.value()
            .map((selected) => options.find((option) => option.value === selected))
            .filter((option): option is SelectFieldOption => option !== undefined);
    });

    /** what the closed trigger reads: the single label, a count, or the placeholder */
    protected readonly triggerLabel = computed(() => {
        const selected = this.selectedOptions();
        if (selected.length === 0) {
            return this.placeholder();
        }

        return selected.length === 1 ? selected[0].label : selected.map((option) => option.label).join(', ');
    });

    protected readonly hasSelection = computed(() => this.value().length > 0);

    protected readonly showSearch = computed(() => this.options().length > this.searchThreshold());

    protected readonly visibleOptions = computed(() => {
        const term = this.searchTerm().toLocaleLowerCase();

        return term
            ? this.options().filter((option) => option.label.toLocaleLowerCase().includes(term))
            : this.options();
    });

    /** further selections are refused once the cap is reached, as in the reactive-forms dropdown */
    protected readonly isAtLimit = computed(() => this.value().length >= this.selectionLimit());

    public toggleMenu(): void {
        if (!this.editable()) {
            return;
        }

        this.isOpen.update((open) => !open);
        if (!this.isOpen()) {
            this.searchTerm.set('');
        }
    }

    public closeMenu(): void {
        this.isOpen.set(false);
        this.searchTerm.set('');
    }

    /**
     * Closes the menu on a click outside the component, without a backdrop element
     * covering the page — that used to block scrolling everywhere while the menu was open.
     */
    protected onDocumentClick(event: MouseEvent): void {
        if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target)) {
            this.closeMenu();
        }
    }

    public isSelected(option: SelectFieldOption): boolean {
        return this.value().includes(option.value);
    }

    /**
     * An option can be picked unless it is disabled or the cap stands in the way. The cap
     * only ever blocks a multi-select: a single-select replaces whatever is there, so
     * being "full" must not stop the user choosing something else. Deselecting is always
     * allowed, which is why an already-selected option stays live at the cap.
     *
     * @param option the option to test
     * @returns whether clicking it would do anything
     */
    public isSelectable(option: SelectFieldOption): boolean {
        if (option.disabled) {
            return false;
        }
        if (!this.isMultiSelect()) {
            return true;
        }

        return !this.isAtLimit() || this.isSelected(option);
    }

    /**
     * Toggles an option. A single-select replaces whatever was there and closes the menu;
     * clicking the current selection clears it, which is how the reactive-forms dropdown
     * behaves too.
     *
     * @param option the option that was clicked
     */
    public select(option: SelectFieldOption): void {
        if (!this.editable() || !this.isSelectable(option)) {
            return;
        }

        if (this.isSelected(option)) {
            if (this.deselectable()) {
                this.commit(this.value().filter((entry) => entry !== option.value));
            }
        } else {
            this.applySelection(option);
        }

        if (!this.isMultiSelect()) {
            this.closeMenu();
        }
    }

    /**
     * @param option the tag that was dismissed
     */
    public remove(option: SelectFieldOption): void {
        if (!this.editable()) {
            return;
        }

        this.commit(this.value().filter((entry) => entry !== option.value));
    }

    public onSearch(event: Event): void {
        this.searchTerm.set((<HTMLInputElement>event.target).value);
    }

    /**
     * Opens the scanner and resolves the result to an option by label, as the
     * reactive-forms dropdown does. Unlike a click this only ever *adds* the match —
     * re-scanning something already chosen must not toggle it back off — and a result
     * that matches nothing raises the same notification the legacy widget raises.
     */
    public scan(): void {
        if (!this.editable()) {
            return;
        }

        const fn = this.scanFn();
        if (!fn) {
            return;
        }
        fn()
            .pipe(
                take(1),
                tap((processed) => {
                    const match = this.findOptionByLabel(processed);
                    if (!match) {
                        this.scanNoMatch.emit(processed);
                        return;
                    }
                    if (this.isSelected(match) || !this.isSelectable(match)) {
                        return;
                    }
                    this.applySelection(match);
                    this.closeMenu();
                    this.scanResults.emit(match.value);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    /**
     * @param text the processed scan result
     * @returns the first option whose label contains it, matching case-insensitively
     */
    private findOptionByLabel(text: string): SelectFieldOption | undefined {
        const needle = text.toLocaleLowerCase();

        return this.options().find((option) => option.label.toLocaleLowerCase().includes(needle));
    }

    /**
     * Adds an option to the selection without toggling: a multi-select appends, a
     * single-select replaces.
     *
     * @param option the option to select
     */
    private applySelection(option: SelectFieldOption): void {
        this.commit(this.isMultiSelect() ? [...this.value(), option.value] : [option.value]);
    }

    /**
     * A click is an interaction, even though the trigger never blurs the way an input does.
     *
     * @param next the new selection
     */
    private commit(next: string[]): void {
        this.value.set(next);
        this.touched.set(true);
    }
}
